import React from 'react'
import DOMPurify from 'dompurify'
import { useResultTileContext, useTextContext } from 'hocs'
import AccordionRowsFactory from './AccordionRowsFactory'
import { Accordion, DetailLinkList } from 'components/molecules'
import { OpeningHours, OpeningIndicator } from 'components/atoms'

// define shortcuts for accordions row factory components to improve the readability
const PEQ = () => <AccordionRowsFactory rowType='peq' />
const IQTIG = () => <AccordionRowsFactory rowType='iqtig' />
const Treatment = () => <AccordionRowsFactory rowType='treatment' />
const TreatmentAOP = () => <AccordionRowsFactory rowType='treatment-aop' />
const NoQSR = ({ missingQsr }) => (
  <AccordionRowsFactory rowType='noQsr' missingQsr={ missingQsr } />
)
const QSR = ({ indicator }) => (
  <AccordionRowsFactory rowType='qsr' indicator={ indicator } />
)
const QSRIndicators = () => <AccordionRowsFactory rowType='qsrIndicators' />

const DetailLink = () => {
  const {
    isMobile,
    linkComponent: LinkComponent,
    linkComponentProps,
    result,
  } = useResultTileContext()

  const LinkWrapper = !isMobile ? LinkComponent : 'span'

  return (
    <h4 className='h4'>
      <LinkWrapper
        { ...linkComponentProps }
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(result.locationName),
        }}
      />
    </h4>
  )
}

const Cooperation = () => {
  const { result } = useResultTileContext()

  return (
    !!result?.cooperation && (
      <span
        data-cy-cooperation={ result.cooperation }
        className='result-tile-small-copy copy-light'>
        {result.cooperation}
      </span>
    )
  )
}

const SubjectArea = () => {
  const { result } = useResultTileContext()

  return (
    !!result?.subjectArea && (
      <span className='copy-light' data-cy='subject-area'>
        {result.subjectArea}
      </span>
    )
  )
}

export const ResultTileTitle = ({ result, type }) => {
  const resultTileTitle = () => {
    switch (type) {
      case 'doctors':
        return (
          <>
            <Cooperation />
            {result.docType === 'doctor' ? (
              <DetailLink />
            ) : (
              <h4 className='h4'>{result.title}</h4>
            )}
            <SubjectArea />
          </>
        )

      case 'caresupport':
      case 'dayandnightcare':
        return (
          <>
            <Cooperation />
            <DetailLink />
          </>
        )

      case 'hospitals':
      case 'carehomes':
      case 'careservices':
        return <DetailLink />

      case 'midwives':
      case 'ambulances':
        return (
          <>
            <Cooperation />
            <h4 className='h4'>{result.title}</h4>
          </>
        )

      case 'oncallservices':
        return (
          <>
            <SubjectArea />
            <h4 className='h4'>{result.title}</h4>
          </>
        )

      default:
        return null
    }
  }
  return <>{resultTileTitle()}</>
}

/**
 * get mapped qsr data
 * @param qsrIdentifier (array with qsr keys, contains all possible qsr keys for a search)
 * @param result (entire result object)
 * @param qsrIndex (0 || 1 is skipp render a qsr value)
 * @returns {*}
 */
const QsrIndicators = ({ qsrIdentifier, result, qsrIndex = false }) =>
  qsrIdentifier.map((identifier, index) => {
    // skip if required
    if (!qsrIndex && index === qsrIndex) return null

    const resultQSR = result.qsr.filter(
      (qsr) => qsr.identifier === identifier.identifier
    )

    if (resultQSR.length !== 0) {
      return (
        <AccordionRowsFactory
          key={ index }
          rowType={ 'qsr' }
          result={ result }
          indicator={ resultQSR[0] }
        />
      )
    }
    return <NoQSR key={ index } missingQsr={ identifier } />
  })

/**
 * get the dynamic, sorted data part for hospital tiles
 * part: accordion head
 * @returns {*}
 * @constructor
 */
export const AccordionTitle = () => {
  const { type, qsrIdentifier, currentSort, result, sorting, qsrHelper } =
    useResultTileContext()
  const textContext = useTextContext()

  switch (type) {
    case 'doctors':
    case 'oncallservices':
      if ('doctor' === result.docType || result.type === 'oncallservices') {
        return (
          <OpeningIndicator
            type={ result.openIndicator.state }
            untilMsg={ result.openIndicator.text }
          />
        )
      }
      return (
        <span>
          {result.coreTeam?.length} {textContext['asvTeam.coreTeam']}{' '}
          {result.consultedTeam?.length} {textContext['asvTeam.consultedTeam']}
        </span>
      )

    case 'hospitals':
      return (
        <>
          {/* search without treatment reason (case with the lowest number of dynamic values */}
          {!sorting.isTreatmentReason && <PEQ />}

          {/* with treatment reason, but no AOP und no qsr */}
          {!!sorting.isTreatmentReasonNoQsr && (
            <>
              {currentSort === 'recommendations' && (
                <>
                  <PEQ />
                  <Treatment />
                </>
              )}
              {(currentSort === 'alphabetical' ||
                currentSort === 'treatment_count' ||
                currentSort === 'distance' ||
                currentSort === 'best_result') && (
                <>
                  <Treatment />
                  <IQTIG />
                </>
              )}
              {currentSort === 'iqtig' && (
                <>
                  <IQTIG />
                  <Treatment />
                </>
              )}
            </>
          )}

          {/* (AOP hit) stationary and ambulant cases are provided */}
          {!!sorting.isAOP && (
            <>
              {(currentSort === 'distance' ||
                currentSort === 'best_result' ||
                currentSort === 'alphabetical' ||
                currentSort === 'treatment_count') && (
                <>
                  <Treatment />
                  <TreatmentAOP />
                  {/* aop with qsr */}
                  {qsrIdentifier.length > 0 &&
                    !currentSort.includes('qsr_') && (
                      <QsrIndicators
                        qsrIdentifier={ qsrIdentifier }
                        result={ result }
                      />
                    )}
                </>
              )}

              {currentSort.includes('qsr_') && (
                <>
                  <QsrIndicators
                    qsrIdentifier={ qsrIdentifier }
                    result={ result }
                  />
                  <Treatment />
                </>
              )}

              {currentSort === 'recommendations' && (
                <>
                  <PEQ />
                  <Treatment />
                  <TreatmentAOP />
                </>
              )}

              {currentSort === 'iqtig' && (
                <>
                  <IQTIG />
                  <Treatment />
                  <TreatmentAOP />
                </>
              )}
            </>
          )}

          {/* TreatmentReason / noAOP / minimum on QSR /  rendering depends on selected result sort */}
          {!!sorting.isTreatmentReasonWithQsr && (
            <>
              {/* qsr based sorting */}
              {currentSort.includes('qsr_') && (
                <>
                  {qsrHelper.getResultQsrBySortingKey() ? (
                    <>
                      <QSR
                        indicator={ qsrHelper.getResultQsrBySortingKey()[0] }
                      />
                      {sorting.isSingleQSRIndicator && <QSRIndicators />}
                    </>
                  ) : (
                    <NoQSR
                      missingQsr={ qsrHelper.getMissingResultQsrBySort()[0] }
                    />
                  )}
                  {!sorting.isSingleQSRIndicator && <Treatment />}
                  {sorting.isSingleQSRIndicator && qsrIdentifier.length < 2 && (
                    <Treatment />
                  )}
                </>
              )}

              {currentSort === 'recommendations' && (
                <>
                  <PEQ />
                  <Treatment />
                </>
              )}

              {currentSort === 'iqtig' && (
                <>
                  <IQTIG />
                  <Treatment />
                </>
              )}

              {/* special case (first) M05, M06, M08, M17, M87 && 5-820 */}
              {(currentSort === 'alphabetical' ||
                currentSort === 'distance' ||
                currentSort === 'treatment_count' ||
                currentSort === 'best_result') && (
                <>
                  <Treatment />

                  {/* qsr without single indicator */}
                  {!sorting.isSingleQSRIndicator && (
                    <QsrIndicators
                      qsrIdentifier={ qsrIdentifier }
                      result={ result }
                    />
                  )}

                  {/* qsr single indicator */}
                  {sorting.isSingleQSRIndicator &&
                    (result?.qsr[0] ? (
                      <>
                        <AccordionRowsFactory
                          rowType='qsr'
                          indicator={ result.qsr[0] }
                          { ...result }
                        />
                        <AccordionRowsFactory
                          rowType='qsrIndicators'
                          { ...result }
                        />
                      </>
                    ) : (
                      <NoQSR missingQsr={ qsrIdentifier[0] } />
                    ))}
                </>
              )}
            </>
          )}
        </>
      )
    default:
      return null
  }
}

/**
 * get the dynamic, sorted data part for hospital tiles
 * part: accordion body
 * @returns {*}
 * @constructor
 * @param props
 */
export const AccordionContent = (props) => {
  const { qsrIdentifier, currentSort, result, sorting, qsrHelper } =
    useResultTileContext()
  const textContext = useTextContext()

  const content = () => {
    switch (result.type) {
      case 'hospitals':
        return (
          <>
            {!!sorting.isAOP && (
              <>
                {(currentSort === 'distance' ||
                  currentSort === 'best_result' ||
                  currentSort === 'alphabetical' ||
                  currentSort === 'treatment_count') && (
                  <>
                    <IQTIG />
                    <PEQ />
                  </>
                )}

                {currentSort.includes('qsr_') && (
                  <>
                    <TreatmentAOP />
                    <PEQ />
                  </>
                )}

                {currentSort === 'recommendations' && (
                  <>
                    {qsrIdentifier.length > 0 &&
                      !currentSort.includes('qsr_') && (
                        <QsrIndicators
                          qsrIdentifier={ qsrIdentifier }
                          result={ result }
                        />
                      )}
                    <IQTIG />
                  </>
                )}

                {currentSort === 'iqtig' && (
                  <>
                    {qsrIdentifier.length > 0 &&
                      !currentSort.includes('qsr_') && (
                        <QsrIndicators
                          qsrIdentifier={ qsrIdentifier }
                          result={ result }
                        />
                      )}
                    <PEQ />
                  </>
                )}
              </>
            )}

            {!!sorting.isTreatmentReasonNoQsr && (
              <>
                {currentSort === 'alphabetical' ||
                currentSort === 'treatment_count' ||
                currentSort === 'distance' ||
                currentSort === 'best_result' ||
                currentSort === 'iqtig' ? (
                  <PEQ />
                ) : (
                  <IQTIG />
                )}
              </>
            )}

            {!!sorting.isTreatmentReasonWithQsr && (
              <>
                {/* sortierung: nach einem qsr wert */}
                {/* render the qsr values which are not shown at the accordion title */}
                {currentSort.includes('qsr_') && (
                  <>
                    {/* qsr values which are not rendered in the title element */}
                    {qsrIdentifier.length > 1 && (
                      <>
                        {qsrHelper.getExcludedResultQsrBySortingKey() ? (
                          qsrHelper
                            .getExcludedResultQsrBySortingKey()
                            .map((qsr, index) => (
                              <AccordionRowsFactory
                                key={ index }
                                rowType={ 'qsr' }
                                result={ result }
                                indicator={ qsr }
                              />
                            ))
                        ) : (
                          <AccordionRowsFactory
                            rowType={ 'noQsr' }
                            result={ result }
                            missingQsr={
                              qsrHelper.getMissingResultQsrBySort()[0]
                            }
                          />
                        )}
                      </>
                    )}
                    {/* {sorting.isSingleQSRIndicator && <Treatment/>}*/}
                    <IQTIG />
                    <PEQ />
                  </>
                )}

                {/* sortierung: weiterempfehlung */}
                {currentSort === 'recommendations' && (
                  <>
                    {/* map qsr based all possible qsr identifiers (only more than one qsr [special cases]) */}
                    <QsrIndicators
                      qsrIdentifier={ qsrIdentifier }
                      result={ result }
                    />
                    {sorting.isSingleQSRIndicator && (
                      <AccordionRowsFactory
                        rowType='qsrIndicators'
                        { ...props }
                      />
                    )}
                    <IQTIG />
                  </>
                )}

                {/* sortierung: qualität nach iqtig */}
                {currentSort === 'iqtig' && (
                  <>
                    <QsrIndicators
                      qsrIdentifier={ qsrIdentifier }
                      result={ result }
                    />
                    <PEQ />
                  </>
                )}
                {/* sortierung: alphabetisch, behandlungsfälle, beste qualität ergebnisse, entfernung */}
                {(currentSort === 'alphabetical' ||
                  currentSort === 'treatment_count' ||
                  currentSort === 'best_result' ||
                  currentSort === 'distance') && (
                  <>
                    <IQTIG />
                    <PEQ />
                  </>
                )}
              </>
            )}
          </>
        )

      case 'doctors':
      case 'oncallservices':
        if ('doctor' === result.docType || result.type === 'oncallservices') {
          return (
            <OpeningHours
              openingHours={ result.openingHours }
              showTimeNotice={
                result.type !== 'oncallservices' &&
                result.openingHours instanceof Object &&
                Object.keys(result.openingHours).length > 0
              }
              showAppointmentOnly={ result.byAppointmentOnly }
            />
          )
        }

        return (
          <>
            <DetailLinkList isAsvList items={ result.coreTeam } />
            <Accordion
              label={ textContext['resultTile.asvTeam.accordionTitle'] }
              type={ 'ASV' }
              className='asv-accordion'>
              <DetailLinkList isAsvList items={ result.consultedTeam } />
            </Accordion>
          </>
        )

      default:
        return null
    }
  }

  return <>{content()}</>
}
